import { useGetMyUserQuery } from '@consigli/hooks';
import { logout } from '@consigli/oauth';
import { type FC, type PropsWithChildren, useEffect, useState, useRef } from 'react';

import { AuthCheckingScreen } from '@/layouts/auth-checking-screen';
import { authConfig } from '@/util/oauth/config';

/** Auth will be the callback from OAuth. So where we'll retrieve the token and store it */
export const UserGuard: FC<PropsWithChildren> = ({ children }) => {
  const { data: user, isLoading } = useGetMyUserQuery();
  const [failed, setFailed] = useState(false);
  const failedRef = useRef(false);
  const timeoutRef = useRef<NodeJS.Timeout>();

  const handleLogout = () => {
    // Both the timer below and the isLoading effect can reach this; without the guard the
    // second caller schedules another uncleared logout()
    if (failedRef.current) {
      return;
    }
    failedRef.current = true;
    setFailed(true);
    setTimeout(() => {
      logout(authConfig);
    }, 5000);
  };

  useEffect(() => {
    // Start 30s timer when query begins
    timeoutRef.current = setTimeout(() => {
      if (!user) {
        handleLogout();
      }
    }, 30000);

    // Cleanup timeout when component unmounts or when user data arrives
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [user]);

  // Driven off the absence of a user rather than the query error. The api lib
  // retries at most once, and never 401 or 403, so a failure arrives promptly.
  useEffect(() => {
    if (!isLoading && !user) {
      handleLogout();
    }
  }, [isLoading, user]);

  if (user) {
    return <>{children}</>;
  }

  return <AuthCheckingScreen failed={failed} />;
};
